import React, { FC, useState } from 'react';
import { Row, Col, Button, Modal, Form, Input, message, Popconfirm } from 'antd';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import GridView from '../../../components/elements/GridView';
import { Tag } from '../../../types'; 
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as filterActions from '../../../core/filters/filtersActions';
import * as assetActions from '../../../core/asset/assetActions';
import { getPaginationTotal } from '../../../components/elements/GridView/GridViewBase/Pagination';

interface TagsTabProps {
    tags?: Tag[];
    onCreateTag?: (name: string) => Promise<void>;
    onDeleteTag?: (id: number) => void;
    tagsPagination?: {
        totalRecords: string;
        perPageCount: string;
        currentPage: string;
    };
    setCurrentTabKey?: (id: string) => void;
    onPaginationChange?: (page: number) => void;
}

const TagsTab:FC<TagsTabProps> = ({
    tags = [],
    onCreateTag = () => {},
    onDeleteTag = () => {},
    tagsPagination = {
        totalRecords: '0',
        perPageCount: '10',
        currentPage: '1',
    },
    setCurrentTabKey = () => {},
    onPaginationChange = () => {},
}) => {
    const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
    const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
    const [newTagName, setNewTagName] = useState<string>('');
    const [creatingTag, setCreatingTag] = useState<boolean>(false);

    const dispatch = useDispatch();
    const actionOptions: any = { 
        ...filterActions,
        ...assetActions,
    };
    const actions: any = bindActionCreators(
        actionOptions,
        dispatch,
    );

    const showDeleteConfirm = (tag) => {
        setSelectedTag(tag);
    };

    const handleDelete = () => {
        if (!onDeleteTag) {
            return;
        }

        if (selectedTag && onDeleteTag) {
            onDeleteTag(selectedTag.id);
        }
        setSelectedTag(null);
    };

    const handleCreateTag = async () => {
        if (!onCreateTag) {
            return;
        }

        const trimmed = newTagName.trim();
        if (!trimmed) return message.error('Please input tag name');
        if (trimmed.length > 100) return message.error('Max 100 characters');

        setCreatingTag(true);
        try {
            await onCreateTag(trimmed);
            message.success('Tag created');
            setCreateModalOpen(false);
            setNewTagName('');
        } finally {
            setCreatingTag(false);
        }
    };
    
    const paginationChange = (page) => {
        if (page !== tagsPagination.currentPage) {
            if (onPaginationChange) {
                onPaginationChange(page);
            }
        }
    };

    let data = tags.map(tag => ({
        name: tag.name,
        assets: (
            <Link
                to={{
                    pathname: '/assets/',
                    state: { currentTabKey: '1', data: [] },
                }}
                onClick={() => {
                    actions.setAssetTabFilters({ tags: [tag.id] });
                    setTimeout(() => { setCurrentTabKey('1') }, 500);
                    actions.filterAssetsRequest({ page: 1, searchParams: { tags: [tag.id] } });
                }}
            >
                {tag.assets}
            </Link>
        ),
        created_at: dayjs(tag.created_at).format('DD/MM/YY HH:mm'),
        updated_at: dayjs(tag.updated_at).format('DD/MM/YY HH:mm'),
        actions: (
            <Popconfirm
                title="Are you sure you'd like to delete this tag permanently?"
                onConfirm={handleDelete}
                okText="Delete"
                cancelText="Cancel"
                okType="danger"
                icon={null}
            >
                <Link
                    onClick={() => showDeleteConfirm(tag)}
                    className="btn-actions"
                    data-testid={`tags_delete-${tag.id}`}
                    to={`#`}
                >
                    Delete
                </Link>
            </Popconfirm>
        ),
    }));
    data = data.sort((a, b) => dayjs(a.updated_at).isBefore(b.updated_at) ? 1 : -1);

    return (
        <div 
            className="assets-tab" 
            style={{ paddingLeft: '32px' }}
        >
            <Row style={{
                maxWidth: "50%", 
                position: 'absolute', 
                right: '0px',
            }}>
                <Col
                    span={24}
                    style={{
                        textAlign: 'right',
                        top: 0,
                        right: 0,
                    }}
                >
                    <Button
                        onClick={() => setCreateModalOpen(true)}
                        type="primary"
                        className='page-header-button'
                        style={{ right: '16px' }}
                        data-testid='tags_create-tag-btn'
                    >
                        Create Tag
                    </Button>
                </Col>
            </Row>

            <div className="assets-grid">
                <GridView
                    data={data}
                    sortableColumns={['name', 'created_at', 'updated_at']}
                    pagination={{
                        total: parseInt(tagsPagination.totalRecords, 10),
                        pageSize: parseInt(tagsPagination.perPageCount, 10),
                        current: parseInt(tagsPagination.currentPage),
                        showSizeChanger: false,
                        onChange: paginationChange,
                        showTotal: getPaginationTotal,
                    }}
                />
            </div>

            <Modal
                open={createModalOpen}
                title="Create Tag"
                okText="Create"
                onOk={handleCreateTag}
                confirmLoading={creatingTag}
                cancelButtonProps={{ style: { display: 'none' } }}
                onCancel={() => setCreateModalOpen(false)}
            >
                <br />
                <Form layout="vertical">
                    <Form.Item>
                        <Input
                            placeholder="Enter tag name"
                            value={newTagName}
                            maxLength={50}
                            disabled={creatingTag}
                            onChange={(e) => setNewTagName(e.target.value)}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}

export default TagsTab;